import { LayoutMeasureAdapter, LayoutMeasureContext, MeasurementSource } from "./types";

function createCloneMeasurementSource(
  element: HTMLElement,
  context: LayoutMeasureContext,
): MeasurementSource {
  const measurementWidth = context.contentWidth;
  const clone = element.cloneNode(true) as HTMLElement;
  clone.removeAttribute("string");
  clone.removeAttribute("data-string");
  clone.removeAttribute("string-split");
  clone.removeAttribute("data-string-split");
  clone.removeAttribute("string-id");
  clone.removeAttribute("data-string-id");
  clone.removeAttribute("string-inited");
  clone.classList.remove("-splitted", "-inview", "-restored");

  clone.innerHTML = element.getAttribute("string-split-original-html") ?? element.innerHTML;
  clone.style.setProperty("position", "absolute", "important");
  clone.style.setProperty("visibility", "hidden", "important");
  clone.style.setProperty("pointer-events", "none", "important");
  clone.style.setProperty("left", "0", "important");
  clone.style.setProperty("top", "0", "important");
  clone.style.setProperty("display", "block", "important");
  clone.style.setProperty("width", `${measurementWidth}px`, "important");
  clone.style.setProperty("min-width", `${measurementWidth}px`, "important");
  clone.style.setProperty("max-width", `${measurementWidth}px`, "important");
  clone.style.setProperty("padding", "0", "important");
  clone.style.setProperty("border", "0", "important");
  clone.style.setProperty("margin", "0", "important");
  clone.style.setProperty("transform", "none", "important");
  clone.style.setProperty("scale", "1", "important");

  const parent = element.parentElement ?? document.body;
  parent.appendChild(clone);

  const verticalAlignForAlignItems: Record<string, string> = {
    "flex-start": "top",
    start: "top",
    "flex-end": "bottom",
    end: "bottom",
    center: "middle",
    baseline: "baseline",
    stretch: "middle",
  };
  const rootAlignItems = window.getComputedStyle(element).alignItems;
  const verticalAlign = verticalAlignForAlignItems[rootAlignItems] ?? "middle";
  clone.querySelectorAll("*").forEach((el) => {
    const display = window.getComputedStyle(el).display;
    if (display !== "flex" && display !== "inline-flex") return;
    const htmlEl = el as HTMLElement;
    htmlEl.style.setProperty("display", "inline-flex", "important");
    htmlEl.style.setProperty("vertical-align", verticalAlign, "important");
    htmlEl.querySelectorAll("*").forEach((descendant) => {
      const descendantEl = descendant as HTMLElement;
      const descendantDisplay = window.getComputedStyle(descendantEl).display;
      if (descendantDisplay === "block") {
        descendantEl.style.setProperty("display", "inline-block", "important");
      }
      descendantEl.style.setProperty("vertical-align", verticalAlign, "important");
    });
  });

  const nodeMap = new Map<Node, Node>();
  const originalWalker = document.createTreeWalker(element, NodeFilter.SHOW_ALL);
  const cloneWalker = document.createTreeWalker(clone, NodeFilter.SHOW_ALL);

  let originalNode = originalWalker.currentNode;
  let cloneNode = cloneWalker.currentNode;
  nodeMap.set(originalNode, cloneNode);

  while ((originalNode = originalWalker.nextNode()) && (cloneNode = cloneWalker.nextNode())) {
    nodeMap.set(originalNode, cloneNode);
  }

  return {
    resolveNode(node: Node): Node {
      return nodeMap.get(node) ?? node;
    },
    cleanup(): void {
      clone.remove();
    },
  };
}

export class FlexMeasureAdapter implements LayoutMeasureAdapter {
  readonly id = "flex";

  supports(_element: HTMLElement, context: LayoutMeasureContext): boolean {
    return context.display === "flex" || context.display === "inline-flex";
  }

  createSource(element: HTMLElement, context: LayoutMeasureContext): MeasurementSource {
    return createCloneMeasurementSource(element, context);
  }
}
